import { useState, useEffect } from "react";

import { useAuth } from "../../../contexts/AuthContext";
import { useToast } from "../../../contexts/ToastContext";
import React from "react";
import {
  addToBlacklist,
  getAllBlacklist,
  gethosts,
  revokeBlacklistStatus,
} from "../../../services/blacklistService";

export interface User {
  id: number;
  name: string;
  email: string;
}

interface BlacklistForm {
  host_ids: number[];
  reason: string;
}

export interface Blacklist {
  id: number;
  host_id: string | null;
  reason: string;
  is_active: boolean;
  is_active_label: string;
  blacklisted_by: string;
  blacklisted_at: string;
  blacklisted_at_formatted: string;
  revoked_by: string;
  revoked_at: string;
  revoked_at_formatted: string;
  status_label: string;
  Event: Event;
  blacklistedHost: User | null;
  blacklistedBy: User | null;
  revokedBy: User | null;
}

const useReportListLogic = () => {
  const { isAuthenticated, user } = useAuth();
  const { showSuccessToast, showErrorToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [blacklists, setBlacklists] = useState<Blacklist[]>([]);
  const [hosts, setHosts] = useState<User[]>([]);

  const fetchBlacklists = async () => {
    try {
      setLoading(true);
      const res = await getAllBlacklist();
      setBlacklists(res.data.data);
    } catch (err) {
      console.error("Error fetching bookings:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHosts = async () => {
    try {
      setLoading(true);
      const res = await gethosts();
      setHosts(res.data.data);
    } catch (err) {
      console.error("Error fetching bookings:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlacklists();
    fetchHosts();
  }, []);

  const [blacklistLoading, setBlacklistLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [blacklistForm, setBlacklistForm] = useState<BlacklistForm>({
    host_ids: [],
    reason: "",
  });

  const onInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: keyof BlacklistForm
  ) => {
    setBlacklistForm({ ...blacklistForm, [field]: e.target.value });
  };

  const [showBlacklistDialog, setShowBlacklistDialog] = React.useState(false);

  const showDialog = () => {
    resetForm();
    setShowBlacklistDialog(true);
  };

  const hideDialog = () => {
    resetForm();
    setShowBlacklistDialog(false);
  };

  const resetForm = () => {
    setBlacklistForm({
      host_ids: [],
      reason: "",
    });
    setSubmitted(false);
  };

  const handleBlacklistEvent = async () => {
    setSubmitted(true);

    if (!blacklistForm.host_ids.length || !blacklistForm.reason) {
      return;
    }

    try {
      setBlacklistLoading(true);

      const payload = {
        host_ids: blacklistForm.host_ids,
        reason: blacklistForm.reason,
      };

      const response = await addToBlacklist(payload);

      showSuccessToast(response.data.message);
      fetchBlacklists();
      fetchHosts();
      hideDialog();
    } catch (error: any) {
      showErrorToast(
        error?.response?.data?.message || "Failed to report issue."
      );
    } finally {
      setBlacklistLoading(false);
    }
  };

  const handleRevokeBlackEvent = async (id: number) => {
    setSubmitted(true);

    try {
      setBlacklistLoading(true);

      const payload = {
        host_ids: blacklistForm.host_ids,
        reason: blacklistForm.reason,
      };

      const response = await revokeBlacklistStatus(id);

      showSuccessToast(response.data.message);
      fetchBlacklists();
      fetchHosts();
    } catch (error: any) {
      showErrorToast(
        error?.response?.data?.message || "Failed to report issue."
      );
    } finally {
      setBlacklistLoading(false);
    }
  };

  return {
    blacklists,
    loading,
    blacklistLoading,
    submitted,
    setSubmitted,
    showBlacklistDialog,
    showDialog,
    hideDialog,
    blacklistForm,
    onInputChange,
    handleBlacklistEvent,
    setBlacklistForm,
    hosts,
    handleRevokeBlackEvent
  };
};

export default useReportListLogic;
